import { createContext, useEffect, useState } from 'react';
import * as React from 'react';
import { localStorageService, STORAGE_KEY } from '../services/local-storage.service';
import { TUser } from '../models/common';
import dbService from '../services/database.service';
import authService from 'services/auth.service';

interface IUserContext {
  user: TUser;
  setUser: React.Dispatch<React.SetStateAction<TUser>>;
  signedIn: any;
  setSignedIn: React.Dispatch<React.SetStateAction<any>>;
  currentPage: { title: any; icon: any };
  setCurrentPage: React.Dispatch<React.SetStateAction<{ title: any; icon: any }>>;
  getUser: () => void;
  handleOpenProfileDialog: (tabIndex: number) => void;
  activeTab: number;
  setActiveTab: (value: number) => void;
  openProfileDialog: boolean;
  setOpenProfileDialog: (value: boolean) => void;
}

export const UserContext = createContext<IUserContext>({
  setSignedIn: (value: any) => undefined,
  setUser: (user: any) => undefined,
  signedIn: false,
  user: {},
  currentPage: { title: '', icon: '' },
  setCurrentPage: (value: any) => undefined,
  getUser: () => undefined,
  handleOpenProfileDialog: (tabIndex: number) => undefined,
  activeTab: 0,
  setActiveTab: (value: number) => undefined,
  openProfileDialog: false,
  setOpenProfileDialog: (value: boolean) => undefined,
});

export const UserContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<TUser>({});
  const [signedIn, setSignedIn] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [openProfileDialog, setOpenProfileDialog] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<any>({
    title: 'Home',
    icon: '',
  });

  const token = authService.getToken();
  const userJSONStr = localStorageService.getItem(STORAGE_KEY.USER);
  const userId = userJSONStr ? JSON.parse(userJSONStr)?._id : undefined;

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    if (!user?._id) return;
    localStorageService.setItem(STORAGE_KEY.USER, JSON.stringify(user));
  }, [user]);

  const initialize = async () => {
    if (!token) {
      handleUserSession();
      return;
    }
    handleTokenVerification();
  };

  const handleUserSession = () => {
    if (userId) getUser();
  };

  const handleTokenVerification = async () => {
    try {
      await authService.verifyToken(token!);
      handleUserSession();
    } catch (error) {
      console.error(error);
    }
  };

  const getUser = async () => {
    try {
      const [user] = await dbService.getUsers({ relation: true, filter: { _id: userId } });
      setUser(user);
      localStorageService.setItem(STORAGE_KEY.USER, JSON.stringify(user));
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenProfileDialog = (tabIndex: number) => {
    setActiveTab(tabIndex);
    setOpenProfileDialog(true);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        signedIn,
        setSignedIn,
        currentPage,
        setCurrentPage,
        getUser,
        handleOpenProfileDialog,
        activeTab,
        setActiveTab,
        openProfileDialog,
        setOpenProfileDialog,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
